import { useCallback, useEffect, useState, useMemo } from "react";
import {
  getShopifyProductSearch,
  getShopifyProductVariants,
} from "~src/api/search";
import { Product } from "~src/types/brand";
import debounce from "lodash/debounce";
import * as Sentry from "@sentry/react";

type FetchProps = {
  selectedBrands: string[];
  searchTerm: string;
  page: number;
  pageSize: number;
};

export type ProductSearchContext = {
  data: Product[];
  total?: number;
  loadMore: () => void;
  loading: boolean;
  error: boolean;
};

export const useInfiniteSearchProducts = ({
                                            selectedBrands,
                                            searchTerm,
                                            page,
                                            pageSize,
                                          }: FetchProps): ProductSearchContext => {
  const [data, setData] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(page);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [total, setTotal] = useState<number>();
  const [initialLoad, setInitialLoad] = useState<boolean>(true);

  const fetchProducts = useCallback(
    async (page: number) => {
      setLoading(true);
      try {
        const productResponse = await getShopifyProductSearch(
          selectedBrands.join(","),
          searchTerm,
          page,
          pageSize,
        );
        let searchResults: Product[] = [];
        if (productResponse?.results.length > 0) {
          const productIds: number[] = productResponse.results.map(
            (product) => product.product_id,
          );
          const variantResponse = await getShopifyProductVariants(
            productIds.join(","),
          );
          searchResults = variantResponse.results;
        }
        return {
          searchResults,
          next: !!productResponse.next,
          count: productResponse.count,
        };
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("module", "useInfiniteSearchProducts");
          scope.setExtra("operation", "fetchProducts");
          Sentry.captureException(err);
        });
        setError(true);
        return { searchResults: [], next: false, count: 0 };
      } finally {
        setLoading(false);
      }
    },
    [selectedBrands, searchTerm, pageSize],
  );

  const loadMore = useCallback(async () => {
    if (hasMore && !loading && !initialLoad) {
      setLoading(true);
      const { searchResults, next, count } = await fetchProducts(
        currentPage + 1,
      );
      setData((prevItems) => [...prevItems, ...searchResults]);
      setCurrentPage((prevPage) => prevPage + 1);
      setHasMore(next);
      setTotal(count);
      setLoading(false);
    }
  }, [currentPage, hasMore, loading, initialLoad, fetchProducts]);

  const debouncedFetch = useMemo(
    () =>
      debounce(async () => {
        const { searchResults, next, count } = await fetchProducts(1);
        setData(searchResults);
        setHasMore(next);
        setTotal(count);
        setInitialLoad(false);
      }, 300),
    [fetchProducts],
  );

  useEffect(() => {
    debouncedFetch();
    return () => {
      debouncedFetch.cancel();
    };
  }, [debouncedFetch, searchTerm, selectedBrands]);

  return { data, loadMore, loading, error, total };
};
